import {
  DEFAULT_CURRENCY,
  FLOW_MAX_GOALS_DEFAULT,
  FLOW_MAX_GOALS_MAX,
  FLOW_MAX_GOALS_MIN,
  getCurrencyLabel,
  isValidFlowMaxGoals,
  pickT,
} from "@repo/shared";
import type {
  CancelReasonMode,
  ComplianceApprovalScope,
  FlowStatus,
  UpdateFlowSettingsRequest,
} from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { QrCode } from "lucide-react";
import { useEffect, useState } from "react";
import { QRCodeModal } from "@/components/qr-code-modal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useConfirm } from "@/hooks/use-confirm";
import { ApiError, patch } from "@/lib/api/_client";
import { setFlowStatus } from "@/lib/api/flow-editor";
import { useInvalidateConfiguredAssessmentTypes } from "@/lib/hooks/use-configured-assessment-types";
import { useFinancialSituationModels } from "@/lib/hooks/use-financial-situation-models";
import { usePdfTemplatesByFlow } from "@/lib/hooks/use-pdf-templates";
import { FlowStatusToggle } from "@/pages/settings/flows/flow-status-toggle";
import { T } from "./flow-settings-tab.i18n";

/** The three states the compliance-approval control offers. */
type ComplianceApprovalSetting = "none" | ComplianceApprovalScope;

// QP-184 — flow gating by client type. null = all types allowed; the UI shows
// null as all three checked and saves all-checked back as null (the server
// normalizes the same way).
const ALL_CLIENT_TYPES = ["Person", "Company", "Group"] as const;
const normalizeAllowedTypes = (types: string[]): string[] | null =>
  types.length === 0 || types.length === ALL_CLIENT_TYPES.length ? null : types;

type FlowSettingsTabProps = {
  flow: any;
  riskModels: Array<{ id: string; name: string }>;
  portfolioCollections: Array<{
    id: string;
    name: string;
    riskModelId: string;
    financialSituationModelId: string | null;
    currency: string;
  }>;
};

export function FlowSettingsTab({ flow, riskModels, portfolioCollections }: FlowSettingsTabProps) {
  // Use flow language for all flow editing UI
  const flowLanguage = flow.language || "en_GB";
  const t = pickT(T, flowLanguage);
  const { models: financialSituationModels } = useFinancialSituationModels();
  const [loading, setLoading] = useState(false);
  const { confirm, ConfirmationDialog } = useConfirm();
  const queryClient = useQueryClient();

  // One prefix invalidation refreshes both the mounted ["flows", id] detail
  // query and the flows list, whose 60s staleTime otherwise shows stale
  // name/status after navigating back.
  // QL-361 — repointing the flow to another collection changes its configured
  // assessment types; the PDF-template-keyed copy sits outside the prefix.
  const invalidateConfiguredTypes = useInvalidateConfiguredAssessmentTypes();
  const invalidateFlows = () => {
    queryClient.invalidateQueries({ queryKey: ["flows"] });
    invalidateConfiguredTypes();
  };

  // Form state
  const [name, setName] = useState(flow.name);
  const [description, setDescription] = useState(flow.description || "");
  const [riskModelId, setRiskModelId] = useState<string | null>(flow.riskModelId || null);
  const [portfolioCollectionId, setPortfolioCollectionId] = useState<string | null>(
    flow.portfolioCollectionId || null,
  );
  const [financialSituationModelId, setFinancialSituationModelId] = useState<string | null>(
    flow.financialSituationModelId || null,
  );
  const [allowPublicStart, setAllowPublicStart] = useState<boolean>(flow.allowPublicStart || false);
  const [allowPublicPdfGen, setAllowPublicPdfGen] = useState<boolean>(
    flow.allowPublicPdfGen || false,
  );
  const [allowPublicSigning, setAllowPublicSigning] = useState<boolean>(
    flow.allowPublicSigning || false,
  );
  const [multiGoal, setMultiGoal] = useState<boolean>(flow.multiGoal || false);
  // QL-593 — kept as typed text so the field can be cleared mid-edit; parsed on save.
  const initialMaxGoals = String(flow.maxGoals ?? FLOW_MAX_GOALS_DEFAULT);
  const [maxGoals, setMaxGoals] = useState<string>(initialMaxGoals);
  const maxGoalsValid = isValidFlowMaxGoals(Number(maxGoals)) && maxGoals.trim() !== "";
  const [multiPortfolio, setMultiPortfolio] = useState<boolean>(flow.multiPortfolio || false);
  // QL-21 — one control, two stored fields. "none" means the step is off;
  // otherwise the value IS the scope.
  const [complianceApproval, setComplianceApproval] = useState<ComplianceApprovalSetting>(
    flow.requireComplianceApproval
      ? (flow.complianceApprovalScope as ComplianceApprovalScope) || "all"
      : "none",
  );
  const requireComplianceApproval = complianceApproval !== "none";
  const [requireExternalApproval, setRequireExternalApproval] = useState<boolean>(
    flow.requireExternalApproval || false,
  );
  const [enableEidSigning, setEnableEidSigning] = useState<boolean>(flow.enableEidSigning || false);
  const [enableDigitalSigning, setEnableDigitalSigning] = useState<boolean>(
    flow.enableDigitalSigning || false,
  );
  // QL-263 — only meaningful with an active PDF template on the flow (the
  // server treats "no template" as a failed automation and leaves the instance
  // at submitted). Stays operable while ON so it can always be switched off.
  const [autoGeneratePdfOnSubmit, setAutoGeneratePdfOnSubmit] = useState<boolean>(
    flow.autoGeneratePdfOnSubmit || false,
  );
  const { templates: pdfTemplates, isLoading: pdfTemplatesLoading } = usePdfTemplatesByFlow(
    flow.id,
  );
  // Only active templates count — that is what the server renders from.
  const hasPdfTemplate = pdfTemplates.some((t) => t.isActive);
  const autoPdfLocked = !pdfTemplatesLoading && !hasPdfTemplate && !autoGeneratePdfOnSubmit;
  const [cancelReasonMode, setCancelReasonMode] = useState<CancelReasonMode>(
    flow.cancelReasonMode || "hidden",
  );
  const [status, setStatus] = useState<FlowStatus>(flow.status);
  const isActive = status === "active";
  const statusHint: Record<FlowStatus, string> = {
    inactive: t.statusInactiveHint,
    preview: t.statusPreviewHint,
    active: t.statusActiveHint,
  };
  // The designer's followup choice. What the flow will actually support is
  // derived below: only an active flow can (QL-401), so a status round-trip
  // before saving (active → preview → active) keeps the choice intact.
  const [followupChoice, setFollowupChoice] = useState<boolean>(flow.supportedForFollowup || false);
  const initialAllowedTypes = (): string[] =>
    flow.allowedClientTypes && flow.allowedClientTypes.length > 0
      ? ALL_CLIENT_TYPES.filter((ct) => flow.allowedClientTypes.includes(ct))
      : [...ALL_CLIENT_TYPES];
  const [allowedClientTypes, setAllowedClientTypes] = useState<string[]>(initialAllowedTypes());

  // Inline client-side signing on the public completion page is only possible
  // when no approval gates run on the advisor side (also enforced server-side).
  const hasApprovalGates = requireComplianceApproval || requireExternalApproval;

  // Public link URL (client-side only to avoid hydration mismatch)
  const [publicLinkUrl, setPublicLinkUrl] = useState(`/s/${flow.id}`);
  const [showQrModal, setShowQrModal] = useState(false);

  useEffect(() => {
    // Update with full URL on client
    setPublicLinkUrl(`${window.location.origin}/s/${flow.id}`);
  }, [flow.id]);

  // Filter portfolio collections by the selected risk model and the flow
  // currency (QL-372: a flow only links a collection in its own currency).
  const flowCurrency = flow.currency || DEFAULT_CURRENCY;
  const availablePortfolioCollections = portfolioCollections.filter(
    (pc) => pc.riskModelId === riskModelId && pc.currency === flowCurrency,
  );

  // Reset portfolio collection if risk model changes. Skip while the
  // collections list is still loading (empty) — otherwise the initial render
  // wipes a legitimately selected collection before the data arrives.
  useEffect(() => {
    if (portfolioCollections.length === 0) return;
    if (
      portfolioCollectionId &&
      !availablePortfolioCollections.find((pc) => pc.id === portfolioCollectionId)
    ) {
      setPortfolioCollectionId(null);
    }
  }, [portfolioCollections.length, portfolioCollectionId, availablePortfolioCollections]);

  // The selected collection's platform rules may bind the flow to one FS model
  // (portfolio_collection.financialSituationModelId). The select auto-syncs and
  // locks to it; the same rule is enforced server-side on save.
  const selectedCollection = portfolioCollections.find((pc) => pc.id === portfolioCollectionId);
  const requiredFsModelId = selectedCollection?.financialSituationModelId ?? null;
  const requiredFsModelName = requiredFsModelId
    ? (financialSituationModels.find((m) => m.id === requiredFsModelId)?.name ?? "")
    : null;
  useEffect(() => {
    if (requiredFsModelId && financialSituationModelId !== requiredFsModelId) {
      setFinancialSituationModelId(requiredFsModelId);
    }
  }, [requiredFsModelId, financialSituationModelId]);

  // Reset signing if PDF generation is disabled (signing requires PDF)
  useEffect(() => {
    if (!allowPublicPdfGen && allowPublicSigning) {
      setAllowPublicSigning(false);
    }
  }, [allowPublicPdfGen, allowPublicSigning]);

  // QL-401 — followup support requires an active flow; this mirrors the
  // server's cascade (leaving active forces the gate off) ahead of the save.
  const supportedForFollowup = isActive && followupChoice;

  const statusChanged = status !== flow.status;
  // QL-401 — the server value the followup toggle will differ from AFTER the
  // status call runs: leaving active cascades the gate off server-side, and a
  // non-active flow never has it on, so a save that ends non-active must not
  // re-toggle it (that would try to enable it).
  const expectedServerFollowup = isActive && (flow.supportedForFollowup || false);
  const followupChanged = supportedForFollowup !== expectedServerFollowup;
  const hasSettingsChanges =
    name !== flow.name ||
    description !== (flow.description || "") ||
    riskModelId !== flow.riskModelId ||
    portfolioCollectionId !== flow.portfolioCollectionId ||
    financialSituationModelId !== flow.financialSituationModelId ||
    allowPublicStart !== (flow.allowPublicStart || false) ||
    allowPublicPdfGen !== (flow.allowPublicPdfGen || false) ||
    allowPublicSigning !== (flow.allowPublicSigning || false) ||
    multiGoal !== (flow.multiGoal || false) ||
    (multiGoal && maxGoals !== initialMaxGoals) ||
    multiPortfolio !== (flow.multiPortfolio || false) ||
    requireComplianceApproval !== (flow.requireComplianceApproval || false) ||
    (requireComplianceApproval && complianceApproval !== (flow.complianceApprovalScope || "all")) ||
    requireExternalApproval !== (flow.requireExternalApproval || false) ||
    enableEidSigning !== (flow.enableEidSigning || false) ||
    enableDigitalSigning !== (flow.enableDigitalSigning || false) ||
    autoGeneratePdfOnSubmit !== (flow.autoGeneratePdfOnSubmit || false) ||
    cancelReasonMode !== (flow.cancelReasonMode || "hidden") ||
    JSON.stringify(normalizeAllowedTypes(allowedClientTypes)) !==
      JSON.stringify(normalizeAllowedTypes(initialAllowedTypes()));
  const hasChanges = statusChanged || followupChanged || hasSettingsChanges;

  const handleSave = async () => {
    if (!name.trim()) {
      await confirm({
        title: t.validationError,
        description: t.flowNameIsRequired,
        confirmText: t.ok,
        type: "info",
      });
      return;
    }
    if (multiGoal && !maxGoalsValid) {
      await confirm({
        title: t.validationError,
        description: t.maxGoalsInvalid,
        confirmText: t.ok,
        type: "info",
      });
      return;
    }

    setLoading(true);

    try {
      if (hasSettingsChanges) {
        await patch(`/internal/flows/${flow.id}/settings`, {
          name: name.trim(),
          description: description.trim() || null,
          riskModelId: riskModelId || null,
          portfolioCollectionId: portfolioCollectionId || null,
          financialSituationModelId: financialSituationModelId || null,
          allowPublicStart,
          allowPublicPdfGen,
          allowPublicSigning,
          multiGoal,
          // The cap only means something on a multi-goal flow; leave the stored
          // value alone otherwise so turning multi-goal back on restores it.
          ...(multiGoal ? { maxGoals: Number(maxGoals) } : {}),
          multiPortfolio,
          requireComplianceApproval,
          // Only meaningful when the step is on; leave the stored scope alone
          // when switching off so turning it back on restores the choice.
          ...(requireComplianceApproval ? { complianceApprovalScope: complianceApproval } : {}),
          requireExternalApproval,
          enableEidSigning,
          enableDigitalSigning,
          autoGeneratePdfOnSubmit,
          cancelReasonMode,
          allowedClientTypes: normalizeAllowedTypes(allowedClientTypes),
        } satisfies UpdateFlowSettingsRequest);
      }

      // The status is a separate endpoint with its own requirement validation.
      // It runs after the settings save so freshly selected models are already
      // persisted when the activation checks read them.
      let statusRefused = false;
      if (statusChanged) {
        try {
          await setFlowStatus(flow.id, { status });
        } catch (err) {
          statusRefused = true;
          setStatus(flow.status);
          const message = err instanceof ApiError ? err.message : t.failedToSaveFlow;
          await confirm({
            title: t.cannotChangeFlowStatus,
            description: message,
            confirmText: t.ok,
            variant: "destructive",
            type: "alert",
          });
        }
      }

      // A save that leaves active cascades followup support off server-side;
      // drop the local choice too, or moving back to active in this mounted
      // tab would quietly re-enable it.
      if (statusChanged && !statusRefused && status !== "active") {
        setFollowupChoice(false);
      }

      // QL-401 — after the status call, so a just-activated flow can enable
      // followup support in the same save. The server validates the enable
      // prerequisites (active + collection + goal/portfolio page structure).
      // Skipped when the status was refused: the followup change was made
      // against that status and would only be refused a second time.
      if (followupChanged && !statusRefused) {
        try {
          await patch(`/internal/flows/${flow.id}/toggle-supported-for-followup`);
        } catch (err) {
          setFollowupChoice(flow.supportedForFollowup || false);
          const message = err instanceof ApiError ? err.message : t.failedToSaveFlow;
          await confirm({
            title: t.cannotChangeFollowupSupport,
            description: message,
            confirmText: t.ok,
            variant: "destructive",
            type: "alert",
          });
        }
      }

      invalidateFlows();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t.failedToSaveFlow;
      await confirm({
        title: t.error,
        description: message,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>{t.flowInformation}</CardTitle>
            <CardDescription>{t.flowInformationDescription}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              {/* Not `name`: this tab stays mounted behind the Fields and Pages
                  tabs, whose dialogs have their own `name` inputs. */}
              <Label htmlFor="flow-name">
                {t.name} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="flow-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t.clientOnboardingPlaceholder}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t.description}</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t.describePurpose}
                rows={3}
              />
            </div>

            {/* Currency — chosen when the flow is created, fixed after (QL-372) */}
            <div className="space-y-2">
              <Label htmlFor="currency">{t.currency}</Label>
              <Input
                id="currency"
                value={getCurrencyLabel(flow.currency || DEFAULT_CURRENCY, flowLanguage)}
                disabled
                readOnly
              />
              <p className="text-sm text-muted-foreground">{t.currencyDescription}</p>
            </div>

            <div className="space-y-2">
              <Label>{t.allowedClientTypes}</Label>
              <div className="flex items-center gap-6">
                {ALL_CLIENT_TYPES.map((type) => {
                  const isChecked = allowedClientTypes.includes(type);
                  const isLocked = allowedClientTypes.length === 1 && isChecked;
                  return (
                    <div key={type} className="flex items-center gap-2">
                      <Checkbox
                        id={`allowedClientType-${type}`}
                        checked={isChecked}
                        onCheckedChange={(checked) => {
                          setAllowedClientTypes((prev) =>
                            checked
                              ? ALL_CLIENT_TYPES.filter((ct) => prev.includes(ct) || ct === type)
                              : prev.filter((ct) => ct !== type),
                          );
                        }}
                        // QL-36 — the last checked box is locked rather than
                        // ignoring the click. Same shape as the goal-type row and
                        // the supported-languages group in regional-settings.
                        disabled={loading || isLocked}
                      />
                      <Label
                        htmlFor={`allowedClientType-${type}`}
                        className={`text-sm font-normal cursor-pointer${
                          isLocked ? " text-muted-foreground" : ""
                        }`}
                      >
                        {type === "Person"
                          ? t.clientTypePerson
                          : type === "Company"
                            ? t.clientTypeCompany
                            : t.clientTypeGroup}
                      </Label>
                    </div>
                  );
                })}
              </div>
              <p className="text-sm text-muted-foreground">{t.allowedClientTypesDescription}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t.riskAssessmentConfig}</CardTitle>
            <CardDescription>{t.riskAssessmentConfigDescription}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="riskModel">{t.riskModel}</Label>
              <Select
                value={riskModelId || "none"}
                onValueChange={(value) => setRiskModelId(value === "none" ? null : value)}
              >
                <SelectTrigger id="riskModel">
                  <SelectValue placeholder={t.selectRiskModel} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <span className="text-muted-foreground">{t.noRiskModel}</span>
                  </SelectItem>
                  {riskModels.map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">{t.requiredForRiskAndPortfolio}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="portfolioCollection">{t.portfolioCollection}</Label>
              <Select
                value={portfolioCollectionId || "none"}
                onValueChange={(value) => setPortfolioCollectionId(value === "none" ? null : value)}
                disabled={!riskModelId}
              >
                <SelectTrigger id="portfolioCollection">
                  <SelectValue placeholder={t.selectPortfolioCollection} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <span className="text-muted-foreground">{t.noPortfolioCollection}</span>
                  </SelectItem>
                  {availablePortfolioCollections.map((collection) => (
                    <SelectItem key={collection.id} value={collection.id}>
                      {collection.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {!riskModelId ? t.selectRiskModelFirst : t.requiredForPortfolioDisplay}
              </p>
              {riskModelId && (
                <p className="text-sm text-muted-foreground">
                  {t.collectionsInCurrency(flowCurrency)}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t.financialSituationAssessment}</CardTitle>
            <CardDescription>{t.financialSituationAssessmentDescription}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="financialSituationModel">{t.financialSituationModel}</Label>
              <Select
                value={financialSituationModelId || "none"}
                onValueChange={(value) =>
                  setFinancialSituationModelId(value === "none" ? null : value)
                }
                disabled={!!requiredFsModelId}
              >
                <SelectTrigger id="financialSituationModel">
                  <SelectValue placeholder={t.selectModel} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <span className="text-muted-foreground">{t.noModel}</span>
                  </SelectItem>
                  {financialSituationModels.map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {requiredFsModelId
                  ? t.fsModelBoundByCollection(requiredFsModelName || "")
                  : t.requiredForFinSitComponents}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t.multiGoalConfig}</CardTitle>
            <CardDescription>{t.multiGoalConfigDescription}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="multiGoal">{t.enableMultiGoal}</Label>
                <p className="text-sm text-muted-foreground">{t.enableMultiGoalDescription}</p>
              </div>
              <Switch
                id="multiGoal"
                checked={multiGoal}
                onCheckedChange={setMultiGoal}
                disabled={loading}
              />
            </div>
            {multiGoal && (
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor="maxGoals">{t.maxGoals}</Label>
                  <p className="text-sm text-muted-foreground">{t.maxGoalsDescription}</p>
                  {!maxGoalsValid && (
                    <p className="text-sm text-destructive" role="alert">
                      {t.maxGoalsInvalid}
                    </p>
                  )}
                </div>
                <Input
                  id="maxGoals"
                  type="number"
                  inputMode="numeric"
                  min={FLOW_MAX_GOALS_MIN}
                  max={FLOW_MAX_GOALS_MAX}
                  step={1}
                  value={maxGoals}
                  onChange={(e) => setMaxGoals(e.target.value)}
                  disabled={loading}
                  aria-invalid={!maxGoalsValid}
                  className="w-24 shrink-0"
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t.multiPortfolioConfig}</CardTitle>
            <CardDescription>{t.multiPortfolioConfigDescription}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="multiPortfolio">{t.enableMultiPortfolio}</Label>
                <p className="text-sm text-muted-foreground">{t.enableMultiPortfolioDescription}</p>
              </div>
              <Switch
                id="multiPortfolio"
                checked={multiPortfolio}
                onCheckedChange={setMultiPortfolio}
                disabled={loading}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t.processTitle}</CardTitle>
            <CardDescription>{t.processDescription}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* QL-21 — one three-way control over two stored fields: the
                boolean says whether the step is in play, the scope says which
                instances get it. Keeping them separate avoids a data migration
                that a generated migration could not perform. */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="complianceApproval">{t.useComplianceApproval}</Label>
                <p className="text-sm text-muted-foreground">
                  {t.useComplianceApprovalDescription}
                </p>
              </div>
              <Select
                value={complianceApproval}
                onValueChange={(v) => setComplianceApproval(v as ComplianceApprovalSetting)}
                disabled={loading}
              >
                <SelectTrigger id="complianceApproval" className="w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t.complianceApprovalNone}</SelectItem>
                  <SelectItem value="all">{t.complianceApprovalAll}</SelectItem>
                  <SelectItem value="screening_hit">{t.complianceApprovalHits}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="requireExternalApproval">{t.useExternalApproval}</Label>
                <p className="text-sm text-muted-foreground">{t.useExternalApprovalDescription}</p>
              </div>
              <Switch
                id="requireExternalApproval"
                checked={requireExternalApproval}
                onCheckedChange={setRequireExternalApproval}
                disabled={loading}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="enableEidSigning">{t.useEidSigning}</Label>
                <p className="text-sm text-muted-foreground">{t.useEidSigningDescription}</p>
              </div>
              <Switch
                id="enableEidSigning"
                checked={enableEidSigning}
                onCheckedChange={setEnableEidSigning}
                disabled={loading}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="enableDigitalSigning">{t.useDigitalSigning}</Label>
                <p className="text-sm text-muted-foreground">{t.useDigitalSigningDescription}</p>
              </div>
              <Switch
                id="enableDigitalSigning"
                checked={enableDigitalSigning}
                onCheckedChange={setEnableDigitalSigning}
                disabled={loading}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="autoGeneratePdfOnSubmit">{t.autoGeneratePdfOnSubmit}</Label>
                <p className="text-sm text-muted-foreground">
                  {t.autoGeneratePdfOnSubmitDescription}
                </p>
                {autoPdfLocked && (
                  <p className="text-sm text-warning">{t.autoGeneratePdfNoTemplate}</p>
                )}
              </div>
              <Switch
                id="autoGeneratePdfOnSubmit"
                checked={autoGeneratePdfOnSubmit}
                onCheckedChange={setAutoGeneratePdfOnSubmit}
                disabled={loading || autoPdfLocked}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="cancelReasonMode">{t.cancelReasonLabel}</Label>
                <p className="text-sm text-muted-foreground">{t.cancelReasonDescription}</p>
              </div>
              <Select
                value={cancelReasonMode}
                onValueChange={(v) => setCancelReasonMode(v as CancelReasonMode)}
                disabled={loading}
              >
                <SelectTrigger id="cancelReasonMode" className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hidden">{t.cancelReasonHidden}</SelectItem>
                  <SelectItem value="optional">{t.cancelReasonOptional}</SelectItem>
                  <SelectItem value="required">{t.cancelReasonRequired}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t.publicAccess}</CardTitle>
            <CardDescription>{t.publicAccessDescription}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="allowPublicStart">{t.allowPublicStart}</Label>
                <p className="text-sm text-muted-foreground">{t.allowPublicStartDescription}</p>
              </div>
              <Switch
                id="allowPublicStart"
                checked={allowPublicStart}
                onCheckedChange={setAllowPublicStart}
                disabled={loading}
              />
            </div>

            {allowPublicStart && (
              <div className="pt-4 border-t space-y-3">
                {flow.pages &&
                flow.pages.filter((p: any) => p.visibility === "public").length === 0 ? (
                  <div className="rounded-lg bg-warning/10 border border-warning/30 p-3">
                    <p className="text-sm text-foreground">{t.noPublicPagesWarning}</p>
                  </div>
                ) : (
                  <div className="rounded-lg bg-muted p-3">
                    <Label className="text-sm font-medium">{t.publicStartLink}</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <code className="flex-1 text-sm bg-background px-3 py-2 rounded border">
                        {publicLinkUrl}
                      </code>
                      <Button
                        size="icon"
                        tooltip={t.qrCode}
                        variant="outline"
                        onClick={() => setShowQrModal(true)}
                      >
                        <QrCode className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(publicLinkUrl);
                        }}
                      >
                        {t.copy}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {t.sharePublicLinkDescription}
                    </p>
                  </div>
                )}

                {/* Completion page options */}
                <div className="pt-4 border-t space-y-4">
                  <Label className="text-sm font-medium">{t.completionPageOptions}</Label>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="allowPublicPdfGen">{t.allowPdfGeneration}</Label>
                      <p className="text-sm text-muted-foreground">
                        {t.allowPdfGenerationDescription}
                      </p>
                    </div>
                    <Switch
                      id="allowPublicPdfGen"
                      checked={allowPublicPdfGen}
                      onCheckedChange={setAllowPublicPdfGen}
                      disabled={loading}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="allowPublicSigning">{t.allowDocumentSigning}</Label>
                      <p className="text-sm text-muted-foreground">
                        {hasApprovalGates
                          ? t.publicSigningBlockedByGates
                          : allowPublicPdfGen
                            ? t.allowDocumentSigningDescription
                            : t.enablePdfFirst}
                      </p>
                    </div>
                    <Switch
                      id="allowPublicSigning"
                      checked={allowPublicSigning && !hasApprovalGates}
                      onCheckedChange={setAllowPublicSigning}
                      disabled={loading || !allowPublicPdfGen || hasApprovalGates}
                    />
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* QL-401 — placed directly above Flow Status so Active stays the last
            card on the page. */}
        <Card>
          <CardHeader>
            <CardTitle>{t.followupTitle}</CardTitle>
            <CardDescription>{t.followupDescription}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>{t.supportedForFollowup}</Label>
                <p className="text-sm text-muted-foreground">{t.supportedForFollowupDescription}</p>
              </div>
              <Switch
                aria-label={t.supportedForFollowup}
                checked={supportedForFollowup}
                onCheckedChange={setFollowupChoice}
                disabled={
                  loading || (!supportedForFollowup && (!isActive || !portfolioCollectionId))
                }
              />
            </div>
            {/* UX mirrors of the server's enable rules; the page-structure rule
                is reported by the server's validation message on save. */}
            {!supportedForFollowup && !isActive && (
              <p className="text-sm text-muted-foreground">{t.followupRequiresActive}</p>
            )}
            {!supportedForFollowup && isActive && !portfolioCollectionId && (
              <p className="text-sm text-muted-foreground">{t.followupRequiresCollection}</p>
            )}
            {supportedForFollowup && (!multiGoal || !multiPortfolio) && (
              <p className="text-sm text-muted-foreground">{t.followupShapeNote}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t.flowStatus}</CardTitle>
            <CardDescription>{t.flowStatusDescription}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <FlowStatusToggle
                ariaLabel={t.flowStatus}
                value={status}
                onChange={setStatus}
                language={flowLanguage}
                disabled={loading}
              />
              <p className="text-sm text-muted-foreground">{statusHint[status]}</p>
            </div>

            {/* Flow statistics */}
            {flow.pages && flow.pages.length > 0 && (
              <div className="pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  {flow.pages.filter((p: any) => p.visibility === "public").length} {t.publicPages},{" "}
                  {flow.pages.filter((p: any) => p.visibility === "internal").length}{" "}
                  {t.internalPages} {t.pages}
                </p>
                {flow.status !== "active" &&
                  flow.pages.some((p: any) => p.components?.length === 0) && (
                    <p className="text-sm text-warning mt-2">{t.emptyPagesWarning}</p>
                  )}
                {flow.portfolioCollectionId &&
                  !flow.pages.some((p: any) => p.pageType === "portfolio") && (
                    <p className="text-sm text-warning mt-2">{t.noPortfolioPageWarning}</p>
                  )}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => {
              setName(flow.name);
              setDescription(flow.description || "");
              setRiskModelId(flow.riskModelId || null);
              setPortfolioCollectionId(flow.portfolioCollectionId || null);
              setFinancialSituationModelId(flow.financialSituationModelId || null);
              setAllowPublicStart(flow.allowPublicStart || false);
              setAllowPublicPdfGen(flow.allowPublicPdfGen || false);
              setAllowPublicSigning(flow.allowPublicSigning || false);
              setAllowedClientTypes(initialAllowedTypes());
              setStatus(flow.status);
              setFollowupChoice(flow.supportedForFollowup || false);
              setComplianceApproval(
                flow.requireComplianceApproval
                  ? (flow.complianceApprovalScope as ComplianceApprovalScope) || "all"
                  : "none",
              );
            }}
            disabled={!hasChanges || loading}
          >
            {t.reset}
          </Button>
          <Button onClick={handleSave} disabled={!hasChanges} loading={loading}>
            {t.save}
          </Button>
        </div>
      </div>
      <ConfirmationDialog />
      <QRCodeModal
        open={showQrModal}
        onClose={() => setShowQrModal(false)}
        url={publicLinkUrl}
        title={t.publicStartLink}
        downloadFileName={`${flow.name.replace(/\s+/g, "-").toLowerCase()}-public-link-qr`}
      />
    </>
  );
}
